import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { buildSwiperOptions, createSwiperConfigSignature } from '@theme/swiper-slideshow-config';

function parseBoolean(value) {
  return value === 'true';
}

async function getSwiperConstructor() {
  if (window.Swiper) return window.Swiper;

  if (document.readyState !== 'complete') {
    await new Promise((resolve) => {
      window.addEventListener('load', resolve, { once: true });
    });
  }

  return window.Swiper;
}

/**
 * @typedef {Object} SwiperSlideshowRefs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} slides
 * @property {HTMLElement} [pagination]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 */

/** @extends {Component<SwiperSlideshowRefs>} */
class SwiperSlideshowComponent extends Component {
  requiredRefs = ['container'];
  #swiper = null;
  #initializing = false;
  #autoplayEnabled = false;
  #editorPaused = false;
  #configSignature = '';

  async connectedCallback() {
    super.connectedCallback();
    await this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();

    const slideCount = this.refs.slides?.length || 0;

    if (slideCount <= 1) {
      this.#destroy();
      return;
    }

    const nextConfig = this.#getConfig();
    const nextSignature = createSwiperConfigSignature(nextConfig);

    if (!this.#swiper) {
      void this.#initialize();
      return;
    }

    if (nextSignature === this.#configSignature) return;

    const activeIndex = this.#swiper.realIndex || 0;
    this.#destroy();
    void this.#initialize({ initialSlide: activeIndex });
  }

  async #initialize({ initialSlide = 0 } = {}) {
    if (this.#swiper || this.#initializing) return;
    if ((this.refs.slides?.length || 0) <= 1) return;

    this.#initializing = true;

    const Swiper = await getSwiperConstructor();

    if (!this.isConnected || !Swiper) {
      this.#initializing = false;
      return;
    }

    const config = this.#getConfig();
    this.#autoplayEnabled = config.autoplayEnabled;
    this.#configSignature = createSwiperConfigSignature(config);

    this.#swiper = new Swiper(
      this.refs.container,
      {
        ...buildSwiperOptions(config),
        initialSlide: Math.min(initialSlide, this.slideCount - 1),
      }
    );

    this.#initializing = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroy();
  }

  get slideCount() {
    return this.refs.slides?.length || 0;
  }

  selectSlide(index, options = {}) {
    if (!this.#swiper) return;
    if (index < 0 || index >= this.slideCount) return;

    if (options.pauseAutoplay) {
      this.#editorPaused = true;
      this.pauseAutoplay();
    }

    const speed = options.instant ? 0 : this.#swiper.params.speed;

    if (this.#swiper.params.loop) {
      this.#swiper.slideToLoop(index, speed);
      return;
    }

    this.#swiper.slideTo(index, speed);
  }

  pauseAutoplay() {
    this.#swiper?.autoplay?.stop();
  }

  resumeAutoplay() {
    if (!this.#swiper || !this.#autoplayEnabled || this.#editorPaused) return;
    this.#swiper.autoplay?.start();
  }

  clearEditorPause() {
    this.#editorPaused = false;
    this.resumeAutoplay();
  }

  #getConfig() {
    return {
      autoplayEnabled: parseBoolean(this.dataset.autoplay) && !prefersReducedMotion(),
      autoplaySpeed: Number(this.dataset.autoplaySpeed) || 5000,
      hasNavigation: Boolean(this.refs.previous && this.refs.next),
      hasPagination: Boolean(this.refs.pagination),
      loop: parseBoolean(this.dataset.loop),
      refs: this.refs,
      slideCount: this.slideCount,
      slideStatusTemplate: this.dataset.slideStatusTemplate || 'Slide [index]',
      transitionEffect: this.dataset.transitionEffect || 'depth',
      transitionSpeed: Number(this.dataset.transitionSpeed) || undefined,
    };
  }

  #destroy() {
    if (this.#swiper) {
      this.#swiper.destroy(true, true);
      this.#swiper = null;
    }

    this.#configSignature = '';
    this.#initializing = false;
    this.#editorPaused = false;
  }
}

if (!customElements.get('swiper-slideshow-component')) {
  customElements.define('swiper-slideshow-component', SwiperSlideshowComponent);
}
